export type StageState = "done" | "active" | "todo";
export type ScreenStatus = "todo" | "done";
export type AssetType = "panel" | "button" | "icon" | "image";
export type FinalOverlayFit = "cover" | "contain" | "fill";
export type WorkflowStageId = "wireframe" | "style" | "slice";
export type TextExportPolicy = "bake-with-art" | "separate-runtime-text" | "discard-text";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EdgeInsets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface NineSlice {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface StatusGroup {
  group: string;
  key: string;
  states: string[];
}

export interface AtlasSlot {
  slotId: string;
  row: number;
  col: number;
  rect: Rect;
  outputName: string;
  targetSize: {
    w: number;
    h: number;
  };
  role: AssetType | "status" | "tag" | "text-layer";
  state?: string;
  textPolicy: TextExportPolicy;
}

export interface FigmaAssetMeta {
  fileKey?: string;
  fileUrl?: string;
  nodeId: string;
  nodeName?: string;
  name: string;
  type: string;
  page: string;
  parentId?: string;
  parentIds?: string[];
  path?: string[];
  text?: string;
  style?: unknown;
  imageRef?: string;
  scaleMode?: string;
  renderUrl?: string;
}

export interface SemanticTreeNode {
  id: string;
  name: string;
  type: string;
  role: string;
  rect?: Rect;
  sourceRect?: Rect;
  assetId?: string;
  parentId?: string;
  path?: string[];
  text?: string;
  textPolicy?: TextExportPolicy;
  figma?: {
    nodeId?: string;
    nodeName?: string;
    type?: string;
    page?: string;
    componentName?: string;
    style?: unknown;
    imageRef?: string;
    scaleMode?: string;
    renderUrl?: string;
  };
  children: SemanticTreeNode[];
}

export interface SemanticTree {
  version: 1;
  source: "figma" | "artflow" | "mixed";
  rootId: string;
  rootName: string;
  canvas: {
    width: number;
    height: number;
  };
  nodes: SemanticTreeNode[];
}

// AI 元素提取模式："auto" 走 SAM 自动分割，"rect" 强制硬切，"segmentation-mask" 用人工复核过的 mask。
export type AssetExtractionMode = "auto" | "rect" | "segmentation-mask";

// 提取目标类型：前景整体 / 本体去文字 / 仅文字层 / 元素背后的背景。
export type AssetExtractionTarget = "foreground" | "body-no-text" | "text-layer" | "background-behind";

export const EXTRACTION_TARGETS: readonly AssetExtractionTarget[] = ["foreground", "body-no-text", "text-layer", "background-behind"];

export interface ArtAsset {
  assetId: string;
  name?: string;
  type: AssetType;
  source: string;
  rect: Rect;
  sourceRect: Rect;
  targetSize: { w: number; h: number };
  trim: EdgeInsets;
  padding: EdgeInsets;
  atlas: string;
  finalPath: string;
  status: string;
  locked?: boolean;
  hidden?: boolean;
  exportEnabled?: boolean;
  nineSlice?: NineSlice;
  statusGroup?: StatusGroup;
  atlasSlot?: AtlasSlot;
  textPolicy?: TextExportPolicy;
  figma?: FigmaAssetMeta;
  // AI 元素提取(切图阶段)：提取模式、提取目标类型、是否背景层、二次处理后的直出图。
  extractionMode?: AssetExtractionMode;
  extractionTarget?: AssetExtractionTarget;
  isBackground?: boolean;
  // 提示词二次处理的结果图(dataUrl)。导出时服务端优先用它，不再从成稿裁切。
  dataUrl?: string;
}

export interface FinalOverlay {
  src: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  fit: FinalOverlayFit;
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface ReferenceImage {
  id: string;
  url: string;
  label: string;
  role: "style" | "layout" | "reference";
}

export interface ReferenceSet {
  style: string;
  layout: string;
  images: ReferenceImage[];
  prompt: string;
  negative: string;
}

export interface ProjectConfig {
  preset: string;
  engineAdapter: string;
  safeAreaProfile: string;
  outputRoot: string;
  canvas: {
    width: number;
    height: number;
  };
  naming: {
    assetIdCase: string;
    pathCase: string;
  };
  atlas: {
    strategy: string;
    maxSize: number;
  };
}

export interface ProjectPreset {
  id: string;
  name: string;
  engineAdapter: string;
  outputRoot: string;
  canvas: {
    width: number;
    height: number;
  };
  safeAreaProfile: string;
  description: string;
}

export interface UiScreen {
  id: string;
  name: string;
  category: string;
  width: number;
  height: number;
  figmaUrl: string;
  layoutRef: string;
  promptPack: string;
  references?: ReferenceSet;
  referenceImageUrl?: string;
  finalOverlay?: FinalOverlay;
  status: ScreenStatus;
  stageStates: Record<WorkflowStageId, StageState>;
  semanticTree?: SemanticTree;
  assets: ArtAsset[];
}

export interface FigmaInfo {
  fileKey: string;
  nodeId: string;
  url: string;
}

export interface NormalizedDocumentState {
  projectName: string;
  canvasWidth: number;
  canvasHeight: number;
  figmaUrl: string;
  outputRoot: string;
  projectConfig: ProjectConfig;
  projectReferences: ReferenceSet;
  styleRef: string;
  layoutRef: string;
  promptPack: string;
  uiScreens: UiScreen[];
  selectedScreenId: string;
  assets: ArtAsset[];
}

export interface WireframeDocument {
  screen?: Partial<UiScreen>;
  canvas?: {
    width?: number;
    height?: number;
  };
  nodes?: any[];
  assets?: any[];
  frames?: any[];
}

export const currentSchemaVersion = 1;
export const defaultOutputRoot = "game/assets/resources/ui";
export const defaultSafeAreaProfile = "wechat_iphone";
export const defaultFinalOverlayOpacity = 0.66;
export const defaultNegativePrompt = "no baked text, no asset ids, no output names, no grid lines, no fake transparent checkerboard";
export const workflowStageIds: WorkflowStageId[] = ["wireframe", "style", "slice"];
export const visibleWorkflowStageIds: WorkflowStageId[] = workflowStageIds;
export const projectPresets: ProjectPreset[] = [
  {
    id: "generic-2d",
    name: "Generic 2D",
    engineAdapter: "generic",
    outputRoot: defaultOutputRoot,
    canvas: { width: 600, height: 1200 },
    safeAreaProfile: defaultSafeAreaProfile,
    description: "通用 2D/UI 项目，输出到默认资源目录。",
  },
  {
    id: "night-city-defense",
    name: "Night City Defense",
    engineAdapter: "laya",
    outputRoot: "game/assets/resources/ui",
    canvas: { width: 750, height: 1334 },
    safeAreaProfile: "wechat_iphone",
    description: "NCD Laya 项目，服务端根目录应指向 /Users/colin/Work/night-city-defense。",
  },
];

export function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function sanitizeId(value: string, fallback = "item"): string {
  return value.toLowerCase().replace(/[^a-z0-9_/-]/g, "_").replace(/^_+|_+$/g, "") || fallback;
}

export function screenPrefix(screenId: string): string {
  return screenId.replace(/[^a-z0-9_/-]/g, "_").toLowerCase();
}

export function uniqueIdInSet(base: string, usedIds: Set<string>, fallback = "item"): string {
  const cleanBase = sanitizeId(base, fallback);
  let id = cleanBase;
  let index = 2;
  while (usedIds.has(id)) {
    id = `${cleanBase}_${index}`;
    index += 1;
  }
  usedIds.add(id);
  return id;
}

export function zeroInsets(): EdgeInsets {
  return { left: 0, top: 0, right: 0, bottom: 0 };
}

export function createFinalOverlay(src = "", overrides: Partial<FinalOverlay> = {}): FinalOverlay {
  return {
    src,
    visible: overrides.visible ?? true,
    locked: Boolean(overrides.locked),
    opacity: clampNumber(Number(overrides.opacity ?? defaultFinalOverlayOpacity), 0, 1),
    fit: ["cover", "contain", "fill"].includes(String(overrides.fit)) ? overrides.fit as FinalOverlayFit : "cover",
    scale: clampNumber(Number(overrides.scale ?? 1), 0.2, 3),
    offsetX: Number(overrides.offsetX ?? 0),
    offsetY: Number(overrides.offsetY ?? 0),
  };
}

export function normalizeFinalOverlay(value: any, fallbackSrc = ""): FinalOverlay {
  const src = String(value?.src ?? fallbackSrc ?? "");
  return createFinalOverlay(src, {
    visible: value?.visible === undefined ? true : Boolean(value.visible),
    locked: Boolean(value?.locked),
    opacity: Number.isFinite(Number(value?.opacity)) ? Number(value.opacity) : defaultFinalOverlayOpacity,
    fit: value?.fit,
    scale: Number.isFinite(Number(value?.scale)) ? Number(value.scale) : 1,
    offsetX: Number.isFinite(Number(value?.offsetX)) ? Number(value.offsetX) : 0,
    offsetY: Number.isFinite(Number(value?.offsetY)) ? Number(value.offsetY) : 0,
  });
}

export function createDefaultProjectConfig(overrides: Partial<ProjectConfig> = {}): ProjectConfig {
  const preset = projectPresets.find((item) => item.id === overrides.preset);
  const canvas = overrides.canvas ?? preset?.canvas ?? { width: 600, height: 1200 };
  return {
    preset: String(overrides.preset ?? preset?.id ?? "generic-2d"),
    engineAdapter: String(overrides.engineAdapter ?? preset?.engineAdapter ?? "generic"),
    safeAreaProfile: String(overrides.safeAreaProfile ?? preset?.safeAreaProfile ?? defaultSafeAreaProfile),
    outputRoot: String(overrides.outputRoot ?? preset?.outputRoot ?? defaultOutputRoot),
    canvas: {
      width: Number(canvas.width ?? 600),
      height: Number(canvas.height ?? 1200),
    },
    naming: {
      assetIdCase: String(overrides.naming?.assetIdCase ?? "snake_case"),
      pathCase: String(overrides.naming?.pathCase ?? "snake_case"),
    },
    atlas: {
      strategy: String(overrides.atlas?.strategy ?? "by-screen"),
      maxSize: Number(overrides.atlas?.maxSize ?? 2048),
    },
  };
}

export function projectPresetById(id: string): ProjectPreset {
  return projectPresets.find((preset) => preset.id === id) ?? projectPresets[0];
}

export function createProjectConfigFromPreset(id: string, overrides: Partial<ProjectConfig> = {}): ProjectConfig {
  const preset = projectPresetById(id);
  return createDefaultProjectConfig({
    ...overrides,
    preset: preset.id,
    engineAdapter: preset.engineAdapter,
    safeAreaProfile: preset.safeAreaProfile,
    outputRoot: preset.outputRoot,
    canvas: overrides.canvas ?? preset.canvas,
  });
}

export function normalizeProjectConfig(value: any, document: any = {}, fallbackCanvas = { width: 600, height: 1200 }): ProjectConfig {
  const canvas = value?.canvas ?? document.canvas ?? {};
  return createDefaultProjectConfig({
    preset: value?.preset,
    engineAdapter: value?.engineAdapter,
    safeAreaProfile: value?.safeAreaProfile ?? document.canvas?.safeAreaProfile,
    outputRoot: value?.outputRoot ?? document.outputRoot,
    canvas: {
      width: Number(canvas.width ?? document.canvas?.width ?? fallbackCanvas.width),
      height: Number(canvas.height ?? document.canvas?.height ?? fallbackCanvas.height),
    },
    naming: value?.naming,
    atlas: value?.atlas,
  });
}

export function createDefaultStageStates(active: WorkflowStageId = "wireframe"): Record<WorkflowStageId, StageState> {
  const nextActive = active;
  return {
    wireframe: nextActive === "wireframe" ? "active" : "done",
    style: nextActive === "style" ? "active" : nextActive === "wireframe" ? "todo" : "done",
    slice: nextActive === "slice" ? "active" : "todo",
  };
}

export function normalizeStageStates(value: any): Record<WorkflowStageId, StageState> {
  const fallback = createDefaultStageStates();
  const validStates = new Set<StageState>(["done", "active", "todo"]);
  const legacySliceState = value?.slice ?? (value?.preview === "active" || value?.apply === "active" ? "active" : undefined);
  return workflowStageIds.reduce((states, stageId) => ({
    ...states,
    [stageId]: validStates.has(stageId === "slice" ? legacySliceState : value?.[stageId])
      ? (stageId === "slice" ? legacySliceState : value?.[stageId])
      : fallback[stageId],
  }), fallback);
}

export function cloneAssets(source: ArtAsset[]): ArtAsset[] {
  return source.map((asset) => ({
    ...asset,
    rect: { ...asset.rect },
    sourceRect: { ...asset.sourceRect },
    targetSize: { ...asset.targetSize },
    trim: { ...asset.trim },
    padding: { ...asset.padding },
    nineSlice: asset.nineSlice ? { ...asset.nineSlice } : undefined,
    statusGroup: asset.statusGroup ? { ...asset.statusGroup, states: [...asset.statusGroup.states] } : undefined,
    atlasSlot: asset.atlasSlot ? structuredClone(asset.atlasSlot) : undefined,
    figma: asset.figma ? structuredClone(asset.figma) : undefined,
  }));
}

export function normalizeSemanticTree(value: any): SemanticTree | undefined {
  if (!value || typeof value !== "object") return undefined;
  const normalizeNode = (node: any): SemanticTreeNode | null => {
    if (!node || typeof node !== "object") return null;
    const children = Array.isArray(node.children)
      ? node.children.map(normalizeNode).filter(Boolean) as SemanticTreeNode[]
      : [];
    const rect = node.rect || node.sourceRect
      ? normalizeRectValue(node.rect ?? node.sourceRect, { x: 0, y: 0, w: 0, h: 0 })
      : undefined;
    const sourceRect = node.sourceRect
      ? normalizeRectValue(node.sourceRect, rect ?? { x: 0, y: 0, w: 0, h: 0 })
      : rect ? { ...rect } : undefined;
    return {
      id: String(node.id ?? node.assetId ?? node.figma?.nodeId ?? "node"),
      name: String(node.name ?? node.figma?.nodeName ?? node.id ?? "node"),
      type: String(node.type ?? node.figma?.type ?? "unknown"),
      role: String(node.role ?? node.semanticRole ?? node.type ?? "unknown"),
      rect,
      sourceRect,
      assetId: node.assetId ? String(node.assetId) : undefined,
      parentId: node.parentId ? String(node.parentId) : undefined,
      path: Array.isArray(node.path) ? node.path.map((item: unknown) => String(item)) : undefined,
      text: node.text ? String(node.text) : undefined,
      textPolicy: node.textPolicy ? normalizeTextExportPolicy(node.textPolicy) : undefined,
      figma: node.figma ? structuredClone(node.figma) : undefined,
      children,
    };
  };
  const nodes = Array.isArray(value.nodes)
    ? value.nodes.map(normalizeNode).filter(Boolean) as SemanticTreeNode[]
    : [];
  if (!nodes.length) return undefined;
  return {
    version: 1,
    source: ["figma", "artflow", "mixed"].includes(value.source) ? value.source : "mixed",
    rootId: String(value.rootId ?? nodes[0]?.id ?? "root"),
    rootName: String(value.rootName ?? nodes[0]?.name ?? "Root"),
    canvas: {
      width: Number(value.canvas?.width ?? 0),
      height: Number(value.canvas?.height ?? 0),
    },
    nodes,
  };
}

export function normalizeScreenStatus(status: unknown): ScreenStatus {
  return status === "done" ? "done" : "todo";
}

export function createReferenceSet(overrides: Partial<ReferenceSet> = {}): ReferenceSet {
  return {
    style: overrides.style ?? "",
    layout: overrides.layout ?? "",
    images: overrides.images ?? [],
    prompt: overrides.prompt ?? "",
    negative: overrides.negative ?? defaultNegativePrompt,
  };
}

// B3：把参考集裁剪成 AI 安全的形态——参考图只保留 id/url/label/role，
// 显式剔除任何 assetId/runtimeKey/outputName/finalPath 等运行时资源字段。
export function sanitizeReferenceSetForAi(value: Partial<ReferenceSet> = {}): ReferenceSet {
  return {
    style: String(value.style ?? ""),
    layout: String(value.layout ?? ""),
    prompt: String(value.prompt ?? ""),
    negative: String(value.negative ?? ""),
    images: (Array.isArray(value.images) ? value.images : []).map((img) => ({
      id: String(img?.id ?? ""),
      url: String(img?.url ?? ""),
      label: String(img?.label ?? ""),
      role: (["style", "layout", "reference"].includes((img as any)?.role) ? (img as any).role : "reference") as ReferenceImage["role"],
    })),
  };
}

// B3：生成可直接交给 AI 的参考资产导出 JSON（项目级 + 界面级），不含运行时字段。
export function buildReferenceExport(input: {
  projectReferences: Partial<ReferenceSet>;
  screenReferences: Partial<ReferenceSet>;
  screenId: string;
  screenName?: string;
  exportedAt?: string;
}): Record<string, unknown> {
  return {
    schema: "artflow-reference-export",
    version: 1,
    exportedAt: input.exportedAt ?? "",
    screen: { id: input.screenId ?? "", name: input.screenName ?? "" },
    note: "仅含 AI 所需参考信息（参考图 id/url/label/role + 风格/线框/提示词/负面提示词），不含 assetId/runtimeKey/outputName/finalPath 等运行时资源字段。",
    projectReferences: sanitizeReferenceSetForAi(input.projectReferences),
    screenReferences: sanitizeReferenceSetForAi(input.screenReferences),
  };
}

export function normalizeReferenceImage(item: any, index: number): ReferenceImage | null {
  const url = String(item?.url ?? item?.src ?? item ?? "").trim();
  if (!url) return null;
  const role = ["style", "layout", "reference"].includes(item?.role) ? item.role as ReferenceImage["role"] : "reference";
  return {
    id: sanitizeId(String(item?.id ?? item?.label ?? `reference_${index + 1}`), `reference_${index + 1}`),
    url,
    label: String(item?.label ?? item?.name ?? `参考图 ${index + 1}`),
    role,
  };
}

export function normalizeReferenceSet(value: any, fallback: Partial<ReferenceSet> = {}): ReferenceSet {
  const images = Array.isArray(value?.images)
    ? value.images.map((item: any, index: number) => normalizeReferenceImage(item, index)).filter(Boolean) as ReferenceImage[]
    : Array.isArray(value?.referenceImages)
      ? value.referenceImages.map((item: any, index: number) => normalizeReferenceImage(item, index)).filter(Boolean) as ReferenceImage[]
      : fallback.images ?? [];
  return createReferenceSet({
    style: String(value?.style ?? value?.styleRef ?? fallback.style ?? ""),
    layout: String(value?.layout ?? value?.layoutRef ?? fallback.layout ?? ""),
    images,
    prompt: String(value?.prompt ?? value?.promptPack ?? fallback.prompt ?? ""),
    negative: String(value?.negative ?? value?.negativePrompt ?? fallback.negative ?? defaultNegativePrompt),
  });
}

export function normalizeRectValue(value: any, fallback: Rect): Rect {
  return {
    x: Number(value?.x ?? fallback.x),
    y: Number(value?.y ?? fallback.y),
    w: Number(value?.w ?? value?.width ?? fallback.w),
    h: Number(value?.h ?? value?.height ?? fallback.h),
  };
}

export function normalizeInsets(value: any): EdgeInsets {
  return {
    left: Number(value?.left ?? 0),
    top: Number(value?.top ?? 0),
    right: Number(value?.right ?? 0),
    bottom: Number(value?.bottom ?? 0),
  };
}

export function normalizeNineSlice(value: any): NineSlice | undefined {
  if (!value) return undefined;
  return {
    left: Number(value.left ?? 0),
    top: Number(value.top ?? 0),
    right: Number(value.right ?? 0),
    bottom: Number(value.bottom ?? 0),
  };
}

export function defaultNineSliceFor(type: AssetType): NineSlice | undefined {
  if (type === "panel") return { left: 24, top: 24, right: 24, bottom: 24 };
  if (type === "button") return { left: 18, top: 16, right: 18, bottom: 16 };
  return undefined;
}

export function normalizeStatusToken(value: unknown, fallback = "default"): string {
  return sanitizeId(String(value ?? fallback), fallback);
}

export function normalizeStatusGroup(value: any): StatusGroup | undefined {
  if (!value) return undefined;
  const states: string[] = Array.isArray(value.states)
    ? value.states.map((state: unknown) => normalizeStatusToken(state, "")).filter(Boolean)
    : String(value.states ?? value.state ?? "")
        .split(/[,\s]+/)
        .map((state) => normalizeStatusToken(state, ""))
        .filter(Boolean);
  return {
    group: normalizeStatusToken(value.group ?? value.name ?? "default_group", "default_group"),
    key: normalizeStatusToken(value.key ?? value.id ?? "default", "default"),
    states: states.length ? [...new Set(states)] : ["normal"],
  };
}

export function defaultStatusGroupFor(type: AssetType): StatusGroup | undefined {
  if (type === "button") return { group: "button", key: "default", states: ["normal", "pressed", "disabled"] };
  return undefined;
}

export function normalizeAtlasSlot(value: any): AtlasSlot | undefined {
  if (!value) return undefined;
  const rect = normalizeRectValue(value.rect, {
    x: Number(value.x ?? 0),
    y: Number(value.y ?? 0),
    w: Number(value.w ?? value.width ?? 0),
    h: Number(value.h ?? value.height ?? 0),
  });
  if (rect.w <= 0 || rect.h <= 0) return undefined;
  const role = ["panel", "button", "icon", "image", "status", "tag", "text-layer"].includes(value.role) ? value.role : "image";
  return {
    slotId: sanitizeId(String(value.slotId ?? value.id ?? value.outputName ?? "slot"), "slot"),
    row: Number(value.row ?? 0),
    col: Number(value.col ?? 0),
    rect,
    outputName: String(value.outputName ?? value.finalPath ?? value.slotId ?? "slot.png"),
    targetSize: {
      w: Number(value.targetSize?.w ?? value.targetSize?.width ?? rect.w),
      h: Number(value.targetSize?.h ?? value.targetSize?.height ?? rect.h),
    },
    role,
    state: value.state ? String(value.state) : undefined,
    textPolicy: normalizeTextExportPolicy(value.textPolicy),
  };
}

export function normalizeTextExportPolicy(value: unknown): TextExportPolicy {
  if (value === "bake-with-art" || value === "separate-runtime-text" || value === "discard-text") return value;
  if (value === "none") return "discard-text";
  return "separate-runtime-text";
}

export function normalizeAsset(item: any, index: number, options: { source?: string; uniqueId?: (base: string) => string; atlas?: string; finalPathPrefix?: string; selectedScreenId?: string; outputRoot?: string } = {}): ArtAsset {
  const rawId = String(item.assetId ?? item.id ?? item.name ?? `asset_${index + 1}`);
  const assetId = options.uniqueId ? options.uniqueId(rawId) : rawId || `asset_${index + 1}`;
  const rawRect = item.rect ?? item.box ?? item.bounds ?? {
    x: Number(item.x ?? 0),
    y: Number(item.y ?? 0),
    w: Number(item.w ?? item.width ?? 120),
    h: Number(item.h ?? item.height ?? 80),
  };
  const rect = normalizeRectValue(rawRect, { x: 0, y: 0, w: 120, h: 80 });
  const sourceRect = normalizeRectValue(item.sourceRect, rect);
  const type = ["panel", "button", "icon", "image"].includes(item.type) ? item.type : "panel";
  const screenId = options.selectedScreenId || "screen";
  const root = options.outputRoot ?? "";
  const source = String(item.source ?? options.source ?? "manifest");
  const hidden = Boolean(item.hidden);
  const exportEnabled = item.exportEnabled !== undefined
    ? Boolean(item.exportEnabled)
    : item.sliceEnabled !== undefined
      ? Boolean(item.sliceEnabled)
      : true;
  return {
    assetId,
    name: item.name ? String(item.name) : item.figma?.name ? String(item.figma.name) : undefined,
    type: type as AssetType,
    source,
    rect,
    sourceRect,
    targetSize: {
      w: Number(item.targetSize?.w ?? item.targetSize?.width ?? rect.w),
      h: Number(item.targetSize?.h ?? item.targetSize?.height ?? rect.h),
    },
    trim: normalizeInsets(item.trim),
    padding: normalizeInsets(item.padding),
    atlas: String(item.atlas ?? options.atlas ?? `${screenPrefix(screenId)}_screen`),
    finalPath: String(item.runtimeKey ?? item.finalPath ?? `${options.finalPathPrefix ?? screenPrefix(screenId)}/${assetId}.png`).replace(`${root}/`, ""),
    status: String(item.status ?? "layout"),
    locked: Boolean(item.locked),
    hidden,
    exportEnabled,
    nineSlice: normalizeNineSlice(item.nineSlice),
    statusGroup: normalizeStatusGroup(item.statusGroup),
    atlasSlot: normalizeAtlasSlot(item.atlasSlot),
    textPolicy: item.textPolicy === undefined ? undefined : normalizeTextExportPolicy(item.textPolicy),
    figma: item.figma ?? undefined,
    extractionMode: item.extractionMode === "auto" || item.extractionMode === "rect" || item.extractionMode === "segmentation-mask"
      ? item.extractionMode as AssetExtractionMode
      : undefined,
    extractionTarget: EXTRACTION_TARGETS.includes(item.extractionTarget as AssetExtractionTarget)
      ? item.extractionTarget as AssetExtractionTarget
      : undefined,
    isBackground: item.isBackground === true ? true : undefined,
    dataUrl: typeof item.dataUrl === "string" && item.dataUrl ? item.dataUrl : undefined,
  };
}

export function createWireframeDocument(state: {
  selectedScreen: UiScreen | null;
  selectedScreenId: string;
  canvasWidth: number;
  canvasHeight: number;
  assets: ArtAsset[];
}): WireframeDocument {
  const screen: Partial<UiScreen> = state.selectedScreen ?? {
    id: state.selectedScreenId || "untitled_screen",
    name: "未命名界面",
    category: "未分类界面",
  };
  return {
    screen: {
      id: screen.id,
      name: screen.name,
      category: screen.category,
      semanticTree: screen.semanticTree ? structuredClone(screen.semanticTree) : undefined,
    },
    canvas: {
      width: state.canvasWidth,
      height: state.canvasHeight,
    },
    nodes: state.assets.map((asset) => ({
      id: asset.assetId,
      name: asset.name ?? null,
      type: asset.type,
      source: asset.source,
      rect: asset.rect,
      sourceRect: asset.sourceRect,
      targetSize: asset.targetSize,
      trim: asset.trim,
      padding: asset.padding,
      atlas: asset.atlas,
      finalPath: asset.finalPath,
      status: asset.status,
      locked: Boolean(asset.locked),
      hidden: Boolean(asset.hidden),
      exportEnabled: asset.exportEnabled ?? true,
      nineSlice: asset.nineSlice ?? null,
      statusGroup: asset.statusGroup ?? null,
      atlasSlot: asset.atlasSlot ?? null,
      textPolicy: asset.textPolicy ?? null,
      figma: asset.figma ?? null,
      extractionMode: asset.extractionMode ?? null,
      extractionTarget: asset.extractionTarget ?? null,
      isBackground: Boolean(asset.isBackground),
    })),
  };
}

export function normalizeArtFlowDocument(data: any, context: {
  projectName: string;
  canvasWidth: number;
  canvasHeight: number;
  figmaUrl: string;
  outputRoot: string;
  projectReferences: ReferenceSet;
  styleRef: string;
  layoutRef: string;
  promptPack: string;
  selectedScreenId: string;
  currentScreens: UiScreen[];
  currentAssets: ArtAsset[];
  preferredScreenId?: string;
}): NormalizedDocumentState {
  const projectConfig = normalizeProjectConfig(data.config, data, { width: context.canvasWidth, height: context.canvasHeight });
  const projectName = data.project ?? context.projectName;
  const canvasWidth = Number(data.canvas?.width ?? projectConfig.canvas.width ?? context.canvasWidth);
  const canvasHeight = Number(data.canvas?.height ?? projectConfig.canvas.height ?? context.canvasHeight);
  const figmaUrl = data.figma?.url ?? context.figmaUrl;
  const outputRoot = projectConfig.outputRoot;
  const nextProjectConfig = { ...projectConfig, outputRoot, canvas: { width: canvasWidth, height: canvasHeight } };
  const projectReferences = normalizeReferenceSet(data.references, {
    style: data.references?.styleRef ?? context.styleRef,
    layout: data.references?.layoutRef ?? context.layoutRef,
    prompt: data.promptPack ?? context.promptPack,
    negative: data.negativePrompt ?? context.projectReferences.negative,
  });
  const styleRef = projectReferences.style;
  const layoutRef = projectReferences.layout;
  const promptPack = projectReferences.prompt;
  let uiScreens: UiScreen[] = context.currentScreens;
  let selectedScreenId = context.selectedScreenId;

  if (Array.isArray(data.screens) && data.screens.length) {
    const usedScreenIds = new Set<string>();
    uiScreens = data.screens.map((screen: any) => {
      const id = uniqueIdInSet(String(screen.id ?? screen.name ?? "screen"), usedScreenIds, "screen");
      return {
        id,
        name: String(screen.name ?? screen.id),
        category: String(screen.category ?? "未分类界面"),
        width: Number(screen.width ?? data.canvas?.width ?? canvasWidth),
        height: Number(screen.height ?? data.canvas?.height ?? canvasHeight),
        figmaUrl: String(screen.figmaUrl ?? data.figma?.url ?? figmaUrl),
        layoutRef: String(screen.references?.layout ?? screen.layoutRef ?? data.references?.layoutRef ?? layoutRef),
        promptPack: String(screen.references?.prompt ?? screen.promptPack ?? data.promptPack ?? promptPack),
        references: normalizeReferenceSet(screen.references, {
          style: screen.references?.style ?? projectReferences.style,
          layout: screen.layoutRef ?? projectReferences.layout,
          images: screen.referenceImageUrl ? [{ id: "screen_final", url: String(screen.referenceImageUrl), label: "当前成稿", role: "reference" }] : [],
          prompt: screen.promptPack ?? projectReferences.prompt,
          negative: projectReferences.negative,
        }),
        referenceImageUrl: screen.referenceImageUrl ? String(screen.referenceImageUrl) : undefined,
        finalOverlay: normalizeFinalOverlay(screen.finalOverlay, screen.referenceImageUrl),
        status: normalizeScreenStatus(screen.status),
        stageStates: normalizeStageStates(screen.stageStates),
        semanticTree: normalizeSemanticTree(screen.semanticTree),
        assets: Array.isArray(screen.assets)
          ? screen.assets.map((asset: any, index: number) => normalizeAsset(asset, index, { selectedScreenId: id, outputRoot }))
          : [],
      };
    });
    selectedScreenId = uiScreens.some((screen) => screen.id === context.preferredScreenId)
      ? String(context.preferredScreenId)
      : uiScreens[0].id;
  } else if (data.screen || Array.isArray(data.assets)) {
    const legacyScreenId = uniqueIdInSet(String(data.screen?.id ?? data.screen?.name ?? "screen"), new Set<string>(), "screen");
    const legacyAssets = Array.isArray(data.assets)
      ? data.assets.map((asset: any, index: number) => normalizeAsset(asset, index, { atlas: "unpacked", selectedScreenId: legacyScreenId, outputRoot }))
      : [];
    uiScreens = [{
      id: legacyScreenId,
      name: String(data.screen?.name ?? data.screen?.id ?? "旧项目界面"),
      category: String(data.screen?.category ?? "未分类界面"),
      width: canvasWidth,
      height: canvasHeight,
      figmaUrl: String(data.figma?.url ?? figmaUrl),
      layoutRef,
      promptPack,
      references: projectReferences,
      referenceImageUrl: data.referenceImageUrl ? String(data.referenceImageUrl) : undefined,
      finalOverlay: normalizeFinalOverlay(data.finalOverlay, data.referenceImageUrl),
      status: normalizeScreenStatus(data.screen?.status),
      stageStates: createDefaultStageStates(),
      semanticTree: normalizeSemanticTree(data.screen?.semanticTree ?? data.semanticTree),
      assets: legacyAssets,
    }];
    selectedScreenId = legacyScreenId;
  } else if (context.preferredScreenId && uiScreens.some((screen) => screen.id === context.preferredScreenId)) {
    selectedScreenId = context.preferredScreenId;
  }

  const assets = Array.isArray(data.assets)
    ? data.assets.map((item: any, index: number) => normalizeAsset(item, index, { atlas: "unpacked", selectedScreenId, outputRoot }))
    : context.currentAssets;

  return {
    projectName,
    canvasWidth,
    canvasHeight,
    figmaUrl,
    outputRoot,
    projectConfig: nextProjectConfig,
    projectReferences,
    styleRef,
    layoutRef,
    promptPack,
    uiScreens,
    selectedScreenId,
    assets,
  };
}

export function createArtFlowDocument(state: {
  projectName: string;
  selectedScreenId: string;
  canvasWidth: number;
  canvasHeight: number;
  figmaInfo: FigmaInfo;
  projectConfig: ProjectConfig;
  projectReferences: ReferenceSet;
  styleRef: string;
  layoutRef: string;
  promptPack: string;
  outputRoot: string;
  uiScreens: UiScreen[];
  assets: ArtAsset[];
}) {
  const screen = state.uiScreens.find((item) => item.id === state.selectedScreenId) ?? state.uiScreens[0] ?? {
    id: state.selectedScreenId || "screen",
    name: "未命名界面",
    category: "未分类界面",
  };
  const references = createReferenceSet({
    ...state.projectReferences,
    style: state.styleRef,
    layout: state.projectReferences.layout || state.layoutRef,
    prompt: state.projectReferences.prompt,
    negative: state.projectReferences.negative,
  });
  return {
    tool: "ArtFlow",
    schemaVersion: currentSchemaVersion,
    framework: "Svelte + Vite",
    version: "0.1.0-mvp",
    project: state.projectName,
    config: {
      ...state.projectConfig,
      outputRoot: state.outputRoot,
      canvas: { width: state.canvasWidth, height: state.canvasHeight },
    },
    screen: {
      id: screen.id,
      name: screen.name,
      category: screen.category,
    },
    canvas: {
      width: state.canvasWidth,
      height: state.canvasHeight,
      safeAreaProfile: state.projectConfig.safeAreaProfile,
    },
    figma: state.figmaInfo,
    references: {
      ...references,
      styleRef: state.styleRef,
      layoutRef: state.layoutRef,
    },
    promptPack: state.promptPack,
    negativePrompt: state.projectReferences.negative,
    outputRoot: state.outputRoot,
    semanticTree: screen.semanticTree ? structuredClone(screen.semanticTree) : undefined,
    screens: state.uiScreens.map((screen) => ({
      ...screen,
      references: normalizeReferenceSet(screen.references, {
        style: state.projectReferences.style,
        layout: screen.layoutRef,
        images: screen.referenceImageUrl ? [{ id: "screen_final", url: screen.referenceImageUrl, label: "当前成稿", role: "reference" }] : [],
        prompt: screen.promptPack,
        negative: state.projectReferences.negative,
      }),
      assets: cloneAssets(screen.assets ?? []),
    })),
    assets: state.assets.map((asset) => ({
      assetId: asset.assetId,
      screenId: state.selectedScreenId,
      name: asset.name ?? null,
      type: asset.type,
      source: asset.source,
      rect: asset.rect,
      sourceRect: asset.sourceRect,
      targetSize: asset.targetSize,
      trim: asset.trim,
      padding: asset.padding,
      atlas: asset.atlas,
      finalPath: `${state.outputRoot}/${asset.finalPath}`,
      runtimeKey: asset.finalPath,
      status: asset.status,
      locked: Boolean(asset.locked),
      hidden: Boolean(asset.hidden),
      exportEnabled: asset.exportEnabled ?? true,
      nineSlice: asset.nineSlice ?? null,
      statusGroup: asset.statusGroup ?? null,
      atlasSlot: asset.atlasSlot ?? null,
      textPolicy: asset.textPolicy ?? null,
      figma: asset.figma ?? null,
      extractionMode: asset.extractionMode ?? null,
      extractionTarget: asset.extractionTarget ?? null,
      isBackground: Boolean(asset.isBackground),
      dataUrl: asset.dataUrl ?? null,
    })),
  };
}
